import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { detectFileType, ACCEPTED_EXTENSIONS, type FileDetectionResult } from '../../utils/fileDetector';
import { inferUnits, UNIT_OPTIONS, type InferredUnit } from '../../utils/unitInference';
import { detectScreeningColumns, detectPatentColumns } from '../../utils/screeningDetector';
import apiClient from '../../api/client';
import { chemistryApi } from '../../api/chemistry';
import StructureImage from '../chemistry/StructureImage';
import { useUIStore } from '../../stores/uiStore';
import { getStoredToken } from '../../utils/tokenStorage';
import { decodeUnicodeEscapes } from '../../utils/decodeUnicode';

// ─── Types ────────────────────────────────────────────────────────────────

interface UnifiedImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  /** Pre-select a project when opened from a project context */
  projectId?: number;
  /** If a file was dropped globally, pass it in to skip step 1 */
  initialFile?: File | null;
}

interface PreviewData {
  file_id: string;
  type: string;
  count: number;
  // SDF/MOL/SMI
  preview_molecules?: { smiles: string; name?: string | null }[];
  // CSV/XLSX/DWAR
  columns?: { name: string; index: number; sampleValues: any[]; detectedType: string; confidence?: number; canonicalField?: string | null }[];
  sheets?: { name: string; rowCount: number }[];
  preview_rows?: Record<string, any>[];
  // FASTA
  preview_sequences?: { header: string; sequence_preview: string; length: number }[];
  // RXN/RDF
  preview_text?: string;
  // PPTX
  slide_count?: number;
  smiles_found?: string[];
  total_tables?: number;
  // PDB
  atom_count?: number;
  title?: string;
  // PDF
  page_count?: number;
  table_count?: number;
  tables?: { page: number; headers: string[]; rows: string[][]; row_count: number; structure_column_index?: number | null; structure_column_name?: string | null }[];
  // Open Babel conversion
  converted_from?: string;
  // Selectivity panel
  format?: string;
  id_column?: string;
  target_columns?: string[];
  non_target_columns?: string[];
  detected_targets?: number;
  suggested_assay_type?: string;
  suggested_unit?: string;
  has_censored_values?: boolean;
  // Generic
  message?: string;
  archive_entries?: { name: string; type: string; size: number }[];
  archive_skipped?: string[];
}

interface ImportFailure {
  row: number;
  name: string;
  smiles: string;
  error: string;
}

interface ImportResult {
  fileId: number;
  total: number;
  success: number;
  failed: number;
  duplicates: number;
  status?: string;
  failures?: ImportFailure[];
  importJobId?: number;
  // Completion-summary breakdown. Three disjoint outcome classes:
  //   IGNORED — never a compound candidate (slide prose, blank rows, a table
  //             with no structure column). Expected; not an error.
  //   SKIPPED — a real candidate deliberately not registered (already exists).
  //   FAILED  — a real compound that should have imported and did not.
  imported_total?: number;
  imported_with_structure?: number;
  imported_structureless?: number;
  skipped_count?: number;
  skipped_reasons?: Record<string, number>;
  failed_count?: number;
  failed_reasons?: Record<string, number>;
  ignored_count?: number;
  ignored_reasons?: Record<string, number>;
  ignored_details?: string[];
  duplicates_merged?: number;
}

type EnrichStatus = 'idle' | 'started' | 'unavailable';

interface Project {
  id: number;
  name: string;
  description?: string;
  color?: string;
}

type Step = 'drop' | 'preview' | 'mapping' | 'units' | 'project' | 'dedup' | 'progress';

interface DedupMatch {
  incoming_smiles: string;
  incoming_name: string | null;
  existing_smiles: string;
  existing_name: string | null;
  existing_id: number;
}

interface DedupResult {
  total: number;
  match_count: number;
  matches: DedupMatch[];
}

interface CanonicalFieldDef {
  name: string;
  display_name: string;
  data_type: string;
  unit: string | null;
  category: string;
  is_structure: boolean;
}

interface BatchMatchResult {
  column: string;
  canonical_field: string | null;
  display_name: string | null;
  confidence: number;
  data_type: string | null;
  unit: string | null;
  category: string | null;
  default_unit: string | null;
  default_unit_type: string | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  identifier: '\u{1F9EA} Identifiers',
  physicochemical: '\u{2697}\uFE0F Physicochemical',
  bioactivity: '\u{1F52C} Bioactivity',
  druglikeness: '\u{1F48A} Druglikeness',
  status: '\u{1F4CB} Status',
  patent: '\u{1F4DC} Patent',
  general: '\u{1F4C1} General',
};

const CATEGORY_ORDER = ['identifier', 'physicochemical', 'bioactivity', 'druglikeness', 'status', 'patent', 'general'];

// ─── Helpers ──────────────────────────────────────────────────────────────

const getAuthHeaders = (): Record<string, string> => {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const FILE_TYPE_LABELS: Record<string, string> = {
  sdf: 'SDF — Structure-Data File',
  mol: 'MOL — Molfile',
  smi: 'SMILES File',
  csv: 'CSV — Comma-Separated',
  xlsx: 'Excel Spreadsheet',
  rxn: 'RXN — Reaction File',
  rdf: 'RDF — Reaction Data',
  fasta: 'FASTA — Sequences',
  helm: 'HELM — Biopolymer Sequences',
  zip: 'ZIP — Bulk Scientific Files',
  pdb: 'PDB — Protein Structure',
  pptx: 'PowerPoint Presentation',
  dwar: 'DWAR — DataWarrior',
  pdf: 'PDF — Document',
};

// ─── Client-side field synonyms (fallback when server returns low confidence) ─
// Key: lowercase column name → Value: canonical field name
const FIELD_SYNONYMS: Record<string, string> = {
  // Activity type variants → activity_type
  activity_type: 'activity_type',
  standard_type: 'activity_type',
  assay_type: 'activity_type',
  measurement_type: 'activity_type',
  endpoint: 'activity_type',
  result_type: 'activity_type',
  bioactivity_type: 'activity_type',

  // Activity value variants → activity_value
  activity_value: 'activity_value',
  standard_value: 'activity_value',
  value: 'activity_value',
  result: 'activity_value',

  // Activity units variants → activity_units
  activity_units: 'activity_units',
  standard_units: 'activity_units',
  units: 'activity_units',
  unit: 'activity_units',

  // Activity relation variants → activity_relation
  activity_relation: 'activity_relation',
  standard_relation: 'activity_relation',
  relation: 'activity_relation',
  qualifier: 'activity_relation',
  operator: 'activity_relation',
  comparator: 'activity_relation',

  // Target name variants → target_name
  target_name: 'target_name',
  target: 'target_name',
  gene_name: 'target_name',
  gene_symbol: 'target_name',
  protein_name: 'target_name',

  // Structure variants → smiles
  molecule_smiles: 'smiles',
  smiles: 'smiles',
  canonical_smiles: 'smiles',
  compound_smiles: 'smiles',

  // Batch ID variants → batch_id
  batch_id: 'batch_id',
  lot_id: 'batch_id',
  lot_number: 'batch_id',
  sample_id: 'batch_id',

  // Assay description variants → assay_description
  assay_description: 'assay_description',
  description: 'assay_description',
  protocol: 'assay_description',
  method: 'assay_description',

  // Reference variants → reference
  reference: 'reference',
  citation: 'reference',
  source: 'reference',
  doi: 'reference',
  pubmed_id: 'reference',
  pmid: 'reference',

  // Compound name variants → compound_name
  molecule_name: 'compound_name',
  compound_name: 'compound_name',
  compound_id: 'compound_id',
  molecule_id: 'molecule_id',
};

/**
 * Normalise a column header for synonym lookup:
 * lowercase, collapse whitespace/hyphens/dots to underscores.
 */
function normaliseSynonym(col: string): string {
  return col.toLowerCase().replace(/[\s.\-]+/g, '_');
}

// ─── Component ────────────────────────────────────────────────────────────

const UnifiedImportDialog: React.FC<UnifiedImportDialogProps> = ({
  isOpen,
  onClose,
  onComplete,
  projectId,
  initialFile,
}) => {
  const navigate = useNavigate();
  const showNotification = useUIStore((s) => s.showNotification);

  // Step machine
  const [step, setStep] = useState<Step>('drop');
  const [file, setFile] = useState<File | null>(null);
  const [detection, setDetection] = useState<FileDetectionResult | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [echoHandoff, setEchoHandoff] = useState<{ file: File; kind: 'workbook' | 'csv' } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [failureEdits, setFailureEdits] = useState<Record<number, string>>({});
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null); // null = not uploading, 0-100 = uploading

  // Mixed-column CSV warning
  const [csvWarning, setCsvWarning] = useState<string | null>(null);

  // SMILES paste
  const [smilesText, setSmilesText] = useState('');

  // Project assignment
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(projectId || null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // CSV/XLSX/DWAR/PDF column mapping
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [canonicalFields, setCanonicalFields] = useState<CanonicalFieldDef[]>([]);
  const [batchResults, setBatchResults] = useState<Record<string, BatchMatchResult>>({});
  const [columnUnits, setColumnUnits] = useState<Record<string, { unit: string | null; unitType: string | null; displayName: string | null }>>({});
  const [mappingLoading, setMappingLoading] = useState(false);

  // PDF table selection (when multiple tables found)
  const [selectedTableIndex, setSelectedTableIndex] = useState(0);

  // Enrichment toggle
  const [enrichAfterImport, setEnrichAfterImport] = useState(false);
  // Non-blocking enrichment status shown on the completion screen
  const [enrichStatus, setEnrichStatus] = useState<EnrichStatus>('idle');

  // SVG thumbnails for molecule preview
  const [thumbnailSvgs, setThumbnailSvgs] = useState<Record<string, string>>({});

  // Selectivity panel state
  const [panelTargets, setPanelTargets] = useState<Set<string>>(new Set());
  const [panelAssayType, setPanelAssayType] = useState('IC50');
  const [panelUnit, setPanelUnit] = useState('nM');
  const [panelIdColumn, setPanelIdColumn] = useState('');

  // Screening detection / category toggle
  const [screeningResult, setScreeningResult] = useState<{ isScreeningData: boolean; matchedColumns: string[] }>({ isScreeningData: false, matchedColumns: [] });
  const [patentResult, setPatentResult] = useState<{ isPatentData: boolean; matchedColumns: string[] }>({ isPatentData: false, matchedColumns: [] });
  const [addToCompounds, setAddToCompounds] = useState(true);
  const [addToScreens, setAddToScreens] = useState(false);
  const [addToPatents, setAddToPatents] = useState(false);

  // Dedup wizard state
  const [dedupResult, setDedupResult] = useState<DedupResult | null>(null);
  const [dedupStrategy, setDedupStrategy] = useState<'import_all' | 'skip_existing'>('import_all');
  const [dedupChecking, setDedupChecking] = useState(false);
  const [dedupReviewOpen, setDedupReviewOpen] = useState(false);

  // Upload abort + speed tracking
  const uploadAbortRef = useRef<AbortController | null>(null);
  const uploadStartRef = useRef<{ time: number; loaded: number } | null>(null);
  const [uploadSpeed, setUploadSpeed] = useState<number | null>(null); // bytes/sec

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dragCountRef = useRef(0);

  // ── Reset on open/close ─────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setStep('drop');
      setFile(null);
      setDetection(null);
      setPreview(null);
      setEchoHandoff(null);
      setImportResult(null);
      setError(null);
      setIsProcessing(false);
      setUploadPercent(null);
      setUploadSpeed(null);
      uploadAbortRef.current?.abort();
      uploadAbortRef.current = null;
      uploadStartRef.current = null;
      setSmilesText('');
      setColumnMappings({});
      setCanonicalFields([]);
      setBatchResults({});
      setMappingLoading(false);
      setSelectedTableIndex(0);
      setEnrichAfterImport(false);
      setThumbnailSvgs({});
      setCsvWarning(null);
      setDedupResult(null);
      setDedupStrategy('import_all');
      setDedupChecking(false);
      setDedupReviewOpen(false);
      setScreeningResult({ isScreeningData: false, matchedColumns: [] });
      setPatentResult({ isPatentData: false, matchedColumns: [] });
      setAddToCompounds(true);
      setAddToScreens(false);
      setAddToPatents(false);
      setSelectedProjectId(projectId || null);
      setShowCreateProject(false);
      setNewProjectName('');

      // If we got an initial file from a global drop, process it immediately
      if (initialFile) {
        handleFileSelected(initialFile);
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Escape key ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessing) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isProcessing, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // ── Cancel upload ──────────────────────────────────────────────────
  const handleCancelUpload = useCallback(() => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    uploadStartRef.current = null;
    setIsProcessing(false);
    setUploadPercent(null);
    setUploadSpeed(null);
  }, []);

  // ── File selection ──────────────────────────────────────────────────
  const handleFileSelected = useCallback(async (f: File) => {
    setError(null);
    const det = detectFileType(f);
    if (det.type === 'unknown') {
      setError(`Unsupported file type. Accepted formats: SDF, MOL, SMI, CSV, XLSX, RXN, RDF, FASTA, HELM, PDB, ZIP, PPTX, HTML`);
      return;
    }
    if (det.type === 'csv') {
      try {
        const header = (await f.slice(0, 8192).text()).split(/\r?\n/, 1)[0].toLowerCase();
        const isEchoTransferLog = [
          'source_plate_barcode', 'source_well', 'destination_plate_barcode',
          'destination_well', 'transfer_status', 'transferred_volume_nl',
        ].every(column => header.includes(column));
        if (isEchoTransferLog) {
          setEchoHandoff({ file: f, kind: 'csv' });
          return;
        }
      } catch {
        // Detection is advisory; continue through the normal importer if the header cannot be read.
      }
    }
    setFile(f);
    setDetection(det);
    setCsvWarning(null);
    setIsProcessing(true);

    // Pre-check CSV files for inconsistent column counts
    if (det.type === 'csv') {
      try {
        const text = await f.text();
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length > 1) {
          const colCounts = lines.map(l => l.split(',').length);
          const maxCols = Math.max(...colCounts);
          const minCols = Math.min(...colCounts);
          if (maxCols !== minCols) {
            setCsvWarning(
              `This file has inconsistent column counts (${minCols}\u2013${maxCols} columns). ` +
              `It may contain multiple tables separated by a blank line. ` +
              `Only the first table section will be imported.`
            );
          }
        }
      } catch {
        // Non-critical: continue with import
      }
    }

    try {
      const formData = new FormData();
      formData.append('file', f);
      formData.append('file_type', det.type);

      setUploadPercent(0);
      setUploadSpeed(null);
      const abortController = new AbortController();
      uploadAbortRef.current = abortController;
      uploadStartRef.current = { time: Date.now(), loaded: 0 };
      const response = await apiClient.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: abortController.signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadPercent(pct);
            // Calculate upload speed
            const start = uploadStartRef.current;
            if (start) {
              const elapsed = (Date.now() - start.time) / 1000;
              if (elapsed > 0.5) {
                setUploadSpeed(Math.round(progressEvent.loaded / elapsed));
              }
            }
            // When upload finishes, switch to "Analyzing..." phase
            if (pct >= 100) {
              setUploadPercent(null);
              setUploadSpeed(null);
            }
          }
        },
      });
      const data = response.data as PreviewData;
      const echoWorkbookSheets = ['Echo Log', 'Source Lookup', 'Assay Context', 'Plate Layout'];
      const detectedSheets = new Set((data.sheets || []).map(sheet => sheet.name.trim().toLowerCase()));
      const isEchoWorkbook = det.type === 'xlsx' && echoWorkbookSheets.every(name => detectedSheets.has(name.toLowerCase()));
      if (isEchoWorkbook) {
        setEchoHandoff({ file: f, kind: 'workbook' });
        return;
      }
      setPreview(data);

      // Initialize selectivity panel state if detected
      if (data.format === 'selectivity_panel') {
        setPanelTargets(new Set(data.target_columns || []));
        setPanelAssayType(data.suggested_assay_type || 'IC50');
        setPanelUnit(data.suggested_unit || 'nM');
        setPanelIdColumn(data.id_column || '');
      }

      // Column mappings will be set by the FieldMapper batch API on the mapping step.
      // Don't pre-fill from detectedType — it conflicts with canonical field names.

      setStep('preview');

      // Load SVG thumbnails for molecules (SDF/MOL/SMI)
      if (data.preview_molecules && data.preview_molecules.length > 0) {
        const smilesList = data.preview_molecules.map(m => m.smiles).filter(Boolean);
        try {
          const bulkResp = await apiClient.post('/v2/indigo/render_bulk',
            smilesList.map(smi => ({ structure: { value: smi, type: 'RAW' }, width: '120px', height: '80px' }))
          );
          if (bulkResp.data && typeof bulkResp.data === 'object') {
            const svgMap: Record<string, string> = {};
            for (const [smi, item] of Object.entries(bulkResp.data as Record<string, { error?: string; result?: string }>)) {
              if (item.result) svgMap[smi] = atob(item.result);
            }
            setThumbnailSvgs(svgMap);
          }
        } catch {
          // Non-critical: fall back to text display
        }
      }

      // Load SVG thumbnails for CSV/XLSX SMILES columns in preview rows
      if (data.columns && data.preview_rows && data.preview_rows.length > 0) {
        const smiCols = data.columns
          .filter(c => c.detectedType === 'smiles' || c.canonicalField === 'smiles')
          .map(c => c.name);
        if (smiCols.length > 0) {
          const smiSet = new Set<string>();
          for (const row of data.preview_rows.slice(0, 5)) {
            for (const col of smiCols) {
              const v = row[col];
              if (v != null && String(v).length > 3) smiSet.add(String(v));
            }
          }
          const smilesList = Array.from(smiSet);
          if (smilesList.length > 0) {
            try {
              const bulkResp = await apiClient.post('/v2/indigo/render_bulk',
                smilesList.map(smi => ({ structure: { value: smi, type: 'RAW' }, width: '120px', height: '80px' }))
              );
              if (bulkResp.data && typeof bulkResp.data === 'object') {
                const svgMap: Record<string, string> = {};
                for (const [smi, item] of Object.entries(bulkResp.data as Record<string, { error?: string; result?: string }>)) {
                  if (item.result) svgMap[smi] = atob(item.result);
                }
                setThumbnailSvgs(prev => ({ ...prev, ...svgMap }));
              }
            } catch {
              // Non-critical: fall back to text display
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        // User cancelled the upload — silently reset
        setError(null);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to preview file');
      }
    } finally {
      setIsProcessing(false);
      setUploadPercent(null);
      setUploadSpeed(null);
      uploadAbortRef.current = null;
      uploadStartRef.current = null;
    }
  }, [navigate, onClose]);

  // ── SMILES paste ────────────────────────────────────────────────────
  const handleSmilesPaste = useCallback(() => {
    if (!smilesText.trim()) return;
    // Create a virtual .smi file from the pasted text
    const blob = new Blob([smilesText], { type: 'text/plain' });
    const virtualFile = new File([blob], 'pasted_smiles.smi', { type: 'text/plain' });
    handleFileSelected(virtualFile);
  }, [smilesText, handleFileSelected]);

  // ── Drag handlers (for the dropzone, not global) ────────────────────
  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current++;
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountRef.current--;
    if (dragCountRef.current <= 0) {
      setIsDragging(false);
      dragCountRef.current = 0;
    }
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCountRef.current = 0;
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelected(files[0]);
    }
  }, [handleFileSelected]);

  // ── Screening detection ─────────────────────────────────────────────
  useEffect(() => {
    if (!preview) return;
    let headers: string[] = [];
    if (preview.columns) {
      headers = preview.columns.map(c => c.name);
    } else if (preview.preview_molecules && preview.preview_molecules.length > 0) {
      // SDF/MOL: extract property keys from preview molecules
      const keySet = new Set<string>();
      for (const mol of preview.preview_molecules) {
        for (const key of Object.keys(mol)) {
          if (key !== 'smiles') keySet.add(key);
        }
      }
      headers = Array.from(keySet);
    }
    if (headers.length > 0) {
      const result = detectScreeningColumns(headers);
      setScreeningResult(result);
      if (result.isScreeningData) {
        setAddToScreens(true);
      }
      const patResult = detectPatentColumns(headers);
      setPatentResult(patResult);
      if (patResult.isPatentData) {
        setAddToPatents(true);
      }
    }
  }, [preview]);

  // ── FieldMapper data loading ────────────────────────────────────────
  useEffect(() => {
    if (step !== 'mapping' || !preview?.columns) return;
    // Skip if already loaded for these columns
    if (Object.keys(batchResults).length > 0) return;

    const loadFieldMapper = async () => {
      setMappingLoading(true);
      try {
        const headers = getAuthHeaders();
        // Fetch canonical fields + batch match in parallel
        const [fieldsRes, batchRes] = await Promise.all([
          canonicalFields.length > 0
            ? Promise.resolve(null)
            : fetch('/api/v1/mapping/fields', { headers }),
          fetch('/api/v1/mapping/batch', {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ columns: preview.columns!.map(c => c.name) }),
          }),
        ]);

        if (fieldsRes?.ok) {
          const fields: CanonicalFieldDef[] = await fieldsRes.json();
          setCanonicalFields(fields);
        }

        if (batchRes.ok) {
          const data = await batchRes.json();
          const resultMap: Record<string, BatchMatchResult> = {};
          const autoMappings: Record<string, string> = {};
          for (const r of (data.results || []) as BatchMatchResult[]) {
            resultMap[r.column] = r;
            console.log(`[FieldMapper] "${r.column}" → ${r.canonical_field || 'unmapped'} (${Math.round(r.confidence * 100)}%)`);
            // Auto-accept high-confidence matches; fall back to client synonyms; else custom
            if (r.canonical_field && r.confidence >= 0.7) {
              autoMappings[r.column] = r.canonical_field;
            } else {
              // Client-side synonym fallback for common assay column names
              const synonym = FIELD_SYNONYMS[normaliseSynonym(r.column)];
              if (synonym) {
                autoMappings[r.column] = synonym;
                console.log(`[FieldMapper] "${r.column}" → ${synonym} (client synonym fallback)`);
              } else {
                autoMappings[r.column] = 'custom';
              }
            }
          }
          setBatchResults(resultMap);

          // Extract unit information from batch results (prefer canonical default_unit)
          // Skip columns that are categorical (text) or contain unit values — those
          // should never have a unit assigned even if the server matched a canonical field
          const categoricalPatterns = [
            /^activity[\s._-]?type$|^standard[\s._-]?type$|^assay[\s._-]?type$|^measurement[\s._-]?type$|^type$|^endpoint$/i,
            /^activity[\s._-]?relation$|^standard[\s._-]?relation$|^relation$|^qualifier$|^operator$|^comparator$/i,
            /^assay[\s._-]?description$|^description$|^assay[\s._-]?name$|^assay$|^protocol$|^method$|^comments?$/i,
            /^target[\s._-]?name$|^target$|^gene[\s._-]?name$|^gene[\s._-]?symbol$|^protein[\s._-]?name$/i,
            /^molecule[\s._-]?name$|^compound[\s._-]?name$|^compound[\s._-]?id$|^molecule[\s._-]?id$|^name$/i,
            /^molecule[\s._-]?smiles$|^smiles$|^canonical[\s._-]?smiles$|^inchi$|^inchi[\s._-]?key$/i,
            /^batch[\s._-]?id$|^lot[\s._-]?id$|^sample[\s._-]?id$|^plate[\s._-]?id$|^well$/i,
            /^reference$|^citation$|^source$|^doi$|^pubmed[\s._-]?id$|^pmid$/i,
            /^activity[\s._-]?units?$|^standard[\s._-]?units?$|^units?$|^measurement[\s._-]?units?$/i,
          ];
          const isCategoricalCol = (col: string) => categoricalPatterns.some(p => p.test(col));

          const unitMap: Record<string, { unit: string | null; unitType: string | null; displayName: string | null }> = {};
          for (const r of (data.results || []) as BatchMatchResult[]) {
            if (isCategoricalCol(r.column)) continue;
            const effectiveUnit = r.default_unit ?? r.unit;
            const effectiveType = r.default_unit_type ?? r.category ?? null;
            if (effectiveUnit || effectiveType === 'dimensionless') {
              unitMap[r.column] = {
                unit: effectiveUnit,
                unitType: effectiveType,
                displayName: effectiveUnit
                  ? (r.display_name ? `${r.display_name} (${effectiveUnit})` : `${r.column} (${effectiveUnit})`)
                  : null,
              };
            }
          }
          setColumnUnits(unitMap);

          // Apply FieldMapper results — merge with any existing user changes
          setColumnMappings(prev => ({ ...prev, ...autoMappings }));

          // Load structure thumbnails for SMILES columns
          const smilesColNames = Object.entries(autoMappings)
            .filter(([, field]) => field === 'smiles' || field === 'canonical_smiles')
            .map(([col]) => col);
          if (smilesColNames.length > 0 && preview?.columns) {
            const smilesValues: string[] = [];
            for (const colName of smilesColNames) {
              const col = preview.columns.find(c => c.name === colName);
              if (col?.sampleValues) {
                for (const v of col.sampleValues) {
                  if (v != null && String(v).length > 3) smilesValues.push(String(v));
                }
              }
            }
            if (smilesValues.length > 0) {
              try {
                const bulkResp = await apiClient.post('/v2/indigo/render_bulk',
                  smilesValues.map(smi => ({ structure: { value: smi, type: 'RAW' }, width: '120px', height: '80px' }))
                );
                if (bulkResp.data && typeof bulkResp.data === 'object') {
                  const svgMap: Record<string, string> = {};
                  for (const [smi, item] of Object.entries(bulkResp.data as Record<string, { error?: string; result?: string }>)) {
                    if (item.result) svgMap[smi] = atob(item.result);
                  }
                  setThumbnailSvgs(prev => ({ ...prev, ...svgMap }));
                }
              } catch {
                // Non-critical: fall back to text display
              }
            }
          }
        }
      } catch {
        // Non-critical — fall back to client-side detection
      } finally {
        setMappingLoading(false);
      }
    };

    loadFieldMapper();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Project loading ─────────────────────────────────────────────────
  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await fetch('/api/projects/', { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
        // Auto-select: prop > localStorage > first
        if (!selectedProjectId) {
          const lastUsed = localStorage.getItem('import:lastProjectId');
          if (lastUsed && data.some((p: Project) => String(p.id) === lastUsed)) {
            setSelectedProjectId(Number(lastUsed));
          } else if (data.length > 0) {
            setSelectedProjectId(data[0].id);
          }
        }
      }
    } catch {
      // non-critical
    } finally {
      setLoadingProjects(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (step === 'project') loadProjects();
  }, [step, loadProjects]);

  // ── Create project ──────────────────────────────────────────────────
  const handleCreateProject = useCallback(async () => {
    if (!newProjectName.trim()) return;
    setCreatingProject(true);
    try {
      const res = await fetch('/api/projects/', {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProjectName.trim() }),
      });
      if (res.ok) {
        const created = await res.json();
        setProjects(prev => [created, ...prev]);
        const createdId = Number(created.id);
        setSelectedProjectId(createdId);
        localStorage.setItem('import:lastProjectId', String(createdId));
        setShowCreateProject(false);
        setNewProjectName('');
      } else {
        const detail = await res.json().catch(() => null);
        setError(detail?.detail || `Could not create project (${res.status})`);
      }
    } catch (err: any) {
      setError(err?.message || 'Could not create project');
    } finally {
      setCreatingProject(false);
    }
  }, [newProjectName]);

  // ── Commit import ───────────────────────────────────────────────────
  // Check for duplicates before importing — if matches found, show dedup wizard
  const handleCheckDuplicates = useCallback(async () => {
    if (!preview?.file_id || !detection) return;
    if (showCreateProject || creatingProject) {
      setError('Create or cancel the new project before importing.');
      return;
    }
    // Only check for molecule file types with a selected project
    const isMoleculeType = ['sdf', 'mol', 'smi', 'csv', 'xlsx'].includes(detection.type);
    if (!isMoleculeType || !selectedProjectId) {
      // Skip dedup check, go straight to import
      handleCommit();
      return;
    }
    setDedupChecking(true);
    try {
      const formData = new FormData();
      formData.append('file_type', detection.type);
      formData.append('file_id', preview.file_id);
      formData.append('project_id', String(selectedProjectId));
      const response = await apiClient.post('/import/check-duplicates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = response.data as DedupResult;
      if (result.match_count > 0) {
        setDedupResult(result);
        setDedupStrategy('import_all');
        setDedupReviewOpen(false);
        setStep('dedup');
      } else {
        // No duplicates, proceed directly
        handleCommit();
      }
    } catch (err) {
      // If check fails, proceed with import anyway
      console.warn('[Import] Dedup check failed, proceeding:', err);
      handleCommit();
    } finally {
      setDedupChecking(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, detection, selectedProjectId, showCreateProject, creatingProject]);

  const handleCommit = useCallback(async () => {
    if (!preview?.file_id || !detection) return;
    setIsProcessing(true);
    setError(null);
    setEnrichStatus('idle');
    setStep('progress');

    try {
      // Selectivity panel → dedicated endpoint
      if (preview.format === 'selectivity_panel' && selectedProjectId) {
        localStorage.setItem('import:lastProjectId', String(selectedProjectId));
        const response = await apiClient.post('/import/execute-panel', {
          file_id: preview.file_id,
          project_id: selectedProjectId,
          id_column: panelIdColumn,
          target_columns: Array.from(panelTargets),
          assay_type: panelAssayType,
          unit: panelUnit,
        });
        setImportResult({
          fileId: 0,  // No file created for panel import
          total: response.data.records_created,
          success: response.data.records_created,
          failed: 0,
          duplicates: 0,
          ...response.data,
        } as any);
        return;
      }

      const formData = new FormData();
      formData.append('file_type', detection.type);
      formData.append('file_id', preview.file_id);
      if (selectedProjectId) {
        formData.append('project_id', String(selectedProjectId));
      }
      if (detection.type === 'csv' || detection.type === 'xlsx' || detection.type === 'dwar' || detection.type === 'pdf') {
        formData.append('mappings', JSON.stringify(columnMappings));
      }
      if (detection.type === 'pdf') {
        formData.append('table_index', String(selectedTableIndex));
      }
      if (enrichAfterImport) {
        formData.append('enrich', 'true');
      }
      if (Object.keys(columnUnits).length > 0) {
        formData.append('units', JSON.stringify(columnUnits));
      }

      // Build file_categories from checkbox state
      const fileCats: string[] = [];
      if (addToCompounds) fileCats.push('compound');
      if (addToScreens) fileCats.push('screen');
      if (addToPatents) fileCats.push('patent');
      if (fileCats.length > 0) {
        formData.append('file_categories', JSON.stringify(fileCats));
      }
      // Dedup strategy from wizard
      if (dedupStrategy && dedupStrategy !== 'import_all') {
        formData.append('dedup_strategy', dedupStrategy);
      }

      // Save last used project
      if (selectedProjectId) {
        localStorage.setItem('import:lastProjectId', String(selectedProjectId));
      }

      setUploadPercent(0);
      const response = await apiClient.post('/import/commit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadPercent(pct);
            if (pct >= 100) setUploadPercent(null);
          }
        },
      });
      setImportResult(response.data as ImportResult);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Import failed');
    } finally {
      setIsProcessing(false);
      setUploadPercent(null);
    }
  }, [preview, detection, selectedProjectId, columnMappings, columnUnits, selectedTableIndex, enrichAfterImport, addToCompounds, addToScreens, addToPatents, panelIdColumn, panelTargets, panelAssayType, panelUnit, dedupStrategy]);

  const handleRetryFailed = useCallback(async () => {
    if (!importResult?.failures?.length || !importResult.fileId) return;
    setIsRetrying(true);
    try {
      const corrections = importResult.failures.map(f => ({
        row: f.row,
        name: f.name,
        smiles: failureEdits[f.row] !== undefined ? failureEdits[f.row] : f.smiles,
      }));
      const response = await apiClient.post('/import/retry', {
        file_id: importResult.fileId,
        import_job_id: importResult.importJobId,
        corrections,
      });
      const retryResult = response.data;
      // Merge results
      setImportResult(prev => prev ? {
        ...prev,
        success: prev.success + retryResult.success,
        failed: retryResult.failed,
        failures: retryResult.failures || [],
        status: retryResult.failed === 0 ? 'success' : (prev.success + retryResult.success > 0 ? 'partial' : 'failed'),
      } : prev);
      setFailureEdits({});
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Retry failed');
    } finally {
      setIsRetrying(false);
    }
  }, [importResult, failureEdits]);

  // ── Navigate to imported data ───────────────────────────────────────
  const handleGoToData = useCallback(() => {
    if (importResult?.fileId) {
      onComplete?.();
      onClose();
      if (selectedProjectId) {
        navigate(`/chemistry/files?project=${selectedProjectId}&view=files`);
      } else {
        navigate(`/chemistry/file/${importResult.fileId}/editor`, { state: { autoEnrich: enrichAfterImport } });
      }
    }
  }, [importResult, selectedProjectId, navigate, onComplete, onClose, enrichAfterImport]);

  // ── Auto-navigate to editor after successful chemistry import ──────
  const autoNavFired = useRef(false);
  useEffect(() => {
    if (!importResult || importResult.fileId <= 0 || isProcessing || autoNavFired.current) return;

    // Only auto-navigate for chemistry imports (molecules/tabular), not sequences
    const cat = detection?.category;
    if (cat === 'sequences') return;

    // Panel imports have fileId 0, already excluded above

    const isBackground = importResult.status === 'processing';

    // Immediate import: do NOT auto-advance. Stop on the completion summary so
    // the user sees the figures (imported / catalogue / skipped) and proceeds
    // to the grid only by clicking "View compounds →" (handleGoToData).

    // Background processing (CIF, large files) — poll until molecules are ready
    if (isBackground) {
      autoNavFired.current = true;
      let cancelled = false;
      const fileId = String(importResult.fileId);

      const poll = setInterval(async () => {
        if (cancelled) return;
        try {
          const file = await chemistryApi.getFile(fileId);
          const ready = (file.status === 'completed' || file.status === 'completed_with_errors')
            && file.moleculesLibraryCount > 0;
          const failed = file.status === 'error' || file.status === 'failed' || file.status === 'cancelled';

          if (ready) {
            clearInterval(poll);
            if (!cancelled) {
              onComplete?.();
              onClose();
              navigate(`/chemistry/file/${importResult.fileId}/editor`, { state: { autoEnrich: enrichAfterImport } });
            }
          } else if (failed) {
            clearInterval(poll);
            if (!cancelled) {
              showNotification('error', 'File processing failed — no molecules were loaded.');
            }
          }
          // else still processing — keep polling
        } catch {
          // network blip — keep polling
        }
      }, 2000);

      return () => { cancelled = true; clearInterval(poll); };
    }

    // Zero molecules imported with no failures — show a warning
    if (importResult.success === 0 && (!importResult.failures || importResult.failures.length === 0)) {
      showNotification('warning', 'Import completed but no molecules were loaded from this file.');
    }
  }, [importResult, isProcessing, detection, navigate, onComplete, onClose, enrichAfterImport, showNotification]);

  // Reset auto-nav flag when starting a new import
  useEffect(() => {
    if (step === 'drop') {
      autoNavFired.current = false;
    }
  }, [step]);

  const handleImportAnother = useCallback(() => {
    setStep('drop');
    setFile(null);
    setDetection(null);
    setPreview(null);
    setImportResult(null);
    setError(null);
    setIsProcessing(false);
    setSmilesText('');
    setColumnMappings({});
    setBatchResults({});
    setMappingLoading(false);
    setSelectedTableIndex(0);
    setThumbnailSvgs({});
    setCsvWarning(null);
  }, []);

  // ── Auto-enrich after import (only when the toggle is on) ───────────
  // Skip for background-processing files — molecules aren't ready yet,
  // so enrichment/patent endpoints will 404. Enrichment will run from
  // EditorPage once the file is actually loaded with molecules.
  useEffect(() => {
    if (!enrichAfterImport) return;                    // respect the auto-enrich toggle
    if (!importResult || importResult.fileId <= 0) return;
    if (importResult.status === 'processing') return; // not ready yet
    if (importResult.success === 0) return;            // nothing to enrich

    let cancelled = false;
    let fastTimer: ReturnType<typeof setInterval>;
    let patentTimer: ReturnType<typeof setInterval>;

    const pollUntilDone = (
      fileId: string,
      taskId: string,
      mode: 'fast',
    ): Promise<void> =>
      new Promise((resolve, reject) => {
        const timer = setInterval(async () => {
          try {
            const sr = await chemistryApi.getIntelligenceStatus(fileId, taskId);
            if (cancelled) { clearInterval(timer); resolve(); return; }
            if (sr.status === 'completed') {
              clearInterval(timer);
              resolve();
            } else if (sr.status === 'failed') {
              clearInterval(timer);
              reject(new Error(sr.error || `${mode} enrichment failed`));
            }
          } catch (err) {
            if (!cancelled) { clearInterval(timer); reject(err); }
          }
        }, 2000);
        fastTimer = timer;
      });

    (async () => {
      const fileId = String(importResult.fileId);
      setEnrichStatus('started');

      // Phase 1: fast enrichment (targets, pathways, reactions, ADMET)
      try {
        const fastResult = await chemistryApi.startIntelligence(fileId, 'fast');
        if (fastResult.task_id !== 'none') {
          await pollUntilDone(fileId, fastResult.task_id, 'fast');
        }
      } catch (err) {
        // Non-blocking: a 404/failure here must never surface as an import error.
        if (!cancelled) { console.error('Auto-enrich fast error:', err); setEnrichStatus('unavailable'); }
      }

      if (cancelled) return;

      // Phase 2: patent search (via patent_collision.py)
      try {
        const numericFileId = parseInt(fileId, 10);
        const scanResult = await chemistryApi.startPatentScan(numericFileId);
        if (scanResult?.scan_id) {
          // Poll patent scan status until done
          await new Promise<void>((resolve, reject) => {
            patentTimer = setInterval(async () => {
              try {
                const ss = await chemistryApi.getPatentScanStatus(numericFileId);
                if (cancelled) { clearInterval(patentTimer); resolve(); return; }
                if (ss.status === 'completed') { clearInterval(patentTimer); resolve(); }
                else if (ss.status === 'failed') { clearInterval(patentTimer); reject(new Error('Patent scan failed')); }
              } catch (e) { if (!cancelled) { clearInterval(patentTimer); reject(e); } }
            }, 2000);
          });
        }
        if (!cancelled) {
          showNotification('success', 'Enrichment complete — patents, targets, pathways updated');
        }
      } catch (err) {
        if (!cancelled) console.error('Auto-enrich patent error:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (fastTimer) clearInterval(fastTimer);
      if (patentTimer) clearInterval(patentTimer);
    };
  }, [importResult, showNotification, enrichAfterImport]);

  // ── Don't render if closed ──────────────────────────────────────────
  if (!isOpen) return null;

  // ─── Step renderers ─────────────────────────────────────────────────

  const renderDropZone = () => (
    <div className="flex flex-col items-center justify-center flex-1 px-8 py-12">
      {/* Drop area */}
      <div
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={`w-full max-w-xl rounded-2xl border-2 border-dashed p-12 text-center transition-all duration-300 cursor-pointer
          ${isDragging
            ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20/50 scale-[1.02] shadow-lg shadow-teal-500/10'
            : 'border-gray-300 dark:border-gray-600 hover:border-teal-300 dark:hover:border-teal-700 hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-800'
          }`}
        onClick={() => document.getElementById('unified-file-input')?.click()}
      >
        <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 ${
          isDragging ? 'bg-teal-100 dark:bg-teal-900/30 scale-110' : 'bg-gray-100 dark:bg-gray-800'
        }`}>
          <svg className={`w-8 h-8 transition-colors ${isDragging ? 'text-teal-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
        </div>
        <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
          {isDragging ? 'Drop your file here' : 'Drag and drop a file here'}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          or <span className="text-teal-600 font-medium">browse files</span>
        </p>
        <div className="flex flex-wrap justify-center gap-1.5">
          {['SDF', 'CIF', 'CSV', 'XLSX', 'RXN', 'FASTA', 'HELM', 'PDB', 'ZIP', 'PDF', 'PPTX', '.dwar'].map(ext => (
            <span key={ext} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full">{ext}</span>
          ))}
        </div>
        <input
          id="unified-file-input"
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          className="hidden"
          onChange={e => {
            if (e.target.files?.[0]) handleFileSelected(e.target.files[0]);
          }}
        />
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4 my-6 w-full max-w-xl">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 uppercase tracking-wider">or paste SMILES</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* SMILES paste field */}
      <div className="w-full max-w-xl">
        <textarea
          value={smilesText}
          onChange={e => setSmilesText(e.target.value)}
          placeholder="Paste SMILES strings (one per line)&#10;e.g. CCO&#10;c1ccccc1&#10;CC(=O)Oc1ccccc1C(=O)O"
          className="w-full h-28 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 placeholder-gray-400"
        />
        {smilesText.trim() && (
          <button
            onClick={handleSmilesPaste}
            className="mt-2 px-5 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            Import {smilesText.trim().split('\n').filter(l => l.trim()).length} SMILES
          </button>
        )}
      </div>

      {/* Upload / Processing progress */}
      {isProcessing && (
        <div className="mt-6 flex flex-col gap-2">
          <div className="flex items-center gap-3 text-teal-600">
            <div className="w-5 h-5 border-2 border-teal-300 border-t-teal-600 rounded-full animate-spin" />
            <span className="text-sm font-medium">
              {uploadPercent !== null && uploadPercent < 100
                ? `Uploading: ${formatBytes(file ? Math.round(file.size * uploadPercent / 100) : 0)} / ${formatBytes(file?.size || 0)} (${uploadPercent}%)`
                : 'Analyzing file...'
              }
            </span>
            {uploadSpeed !== null && uploadPercent !== null && uploadPercent < 100 && (
              <span className="text-xs text-gray-400">{formatBytes(uploadSpeed)}/s</span>
            )}
          </div>
          {uploadPercent !== null && uploadPercent < 100 && (
            <>
              <div className="ml-8" style={{ width: 200, height: 4, borderRadius: 2, background: 'rgba(13, 148, 136, 0.15)', overflow: 'hidden' }}>
                <div style={{
                  width: `${uploadPercent}%`,
                  height: '100%',
                  borderRadius: 2,
                  background: '#0d9488',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <button
                onClick={handleCancelUpload}
                className="ml-8 text-xs text-gray-400 hover:text-red-500 transition-colors self-start"
              >
                Cancel upload
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderPreview = () => {
    if (!preview || !detection) return null;
    const typeLabel = FILE_TYPE_LABELS[detection.type] || detection.type;

    return (
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* File info header */}
        <div className="flex items-center gap-4 mb-6 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-100 dark:border-teal-800">
          <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 font-bold text-lg">
            {detection.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{file?.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{typeLabel} &middot; {preview.count.toLocaleString()} {detection.category === 'sequences' ? 'sequences' : detection.category === 'reactions' ? 'reactions' : 'items'}</p>
          </div>
          <button
            onClick={() => { setStep('drop'); setFile(null); setPreview(null); setDetection(null); }}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-300"
          >
            Change file
          </button>
        </div>

        {/* Mixed-column CSV warning */}
        {csvWarning && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-700 dark:text-amber-300 text-sm flex items-start gap-2">
            <span className="mt-0.5 flex-shrink-0">&#x26A0;&#xFE0F;</span>
            <span>{csvWarning}</span>
          </div>
        )}

        {/* Type-specific preview */}
        {/* SDF / MOL / SMI - molecule thumbnails */}
        {preview.preview_molecules && preview.preview_molecules.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Structure Preview</h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {preview.preview_molecules.map((mol, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-center">
                  {thumbnailSvgs[mol.smiles] ? (
                    <div
                      className="h-20 flex items-center justify-center"
                      dangerouslySetInnerHTML={{ __html: thumbnailSvgs[mol.smiles] }}
                    />
                  ) : (
                    <div className="h-20 flex items-center justify-center">
                      <StructureImage smiles={mol.smiles} size={80} lazy />
                    </div>
                  )}
                  {mol.name && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">{mol.name}</p>
                  )}
                </div>
              ))}
            </div>
            {preview.count > 12 && (
              <p className="text-xs text-gray-400 mt-2">Showing 12 of {preview.count.toLocaleString()}</p>
            )}
          </div>
        )}

        {/* CSV / XLSX / DWAR - detected columns + sample data */}
        {preview.columns && detection.type !== 'pdf' && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Detected {preview.columns.length} Columns
            </h4>
            <div className="flex flex-wrap gap-2 mb-4">
              {preview.columns.map(col => {
                const mapped = col.detectedType && col.detectedType !== 'unknown';
                return (
                  <span key={col.name} className={`px-2.5 py-1 text-xs rounded-full border ${
                    mapped
                      ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {col.name}
                    {mapped && <span className="ml-1 text-[10px] opacity-70">({col.detectedType})</span>}
                  </span>
                );
              })}
            </div>
            {/* Sample data preview */}
            {preview.preview_rows && preview.preview_rows.length > 0 && (
              <div className="mt-4">
                <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Sample Data</h5>
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {Object.keys(preview.preview_rows[0]).map(key => (
                          <th key={key} className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const colKeys = Object.keys(preview.preview_rows![0]);
                        const smilesColSet = new Set(
                          preview.columns?.filter(c => c.detectedType === 'smiles' || c.canonicalField === 'smiles').map(c => c.name) || []
                        );
                        return preview.preview_rows!.slice(0, 5).map((row, i) => (
                          <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                            {colKeys.map((key, j) => {
                              const val = row[key];
                              const smi = val != null ? String(val) : '';
                              const svg = smilesColSet.has(key) ? thumbnailSvgs[smi] : null;
                              return svg ? (
                                <td key={j} className="px-1 py-1" title={smi}>
                                  <div className="w-[120px] h-[50px] bg-white rounded overflow-hidden flex items-center justify-center"
                                    dangerouslySetInnerHTML={{ __html: svg }} />
                                </td>
                              ) : (
                                <td key={j} className="px-3 py-1.5 text-gray-600 dark:text-gray-400 truncate max-w-[150px]">{smi}</td>
                              );
                            })}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* DWAR-specific note */}
        {detection.type === 'dwar' && preview.columns && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              <strong>DataWarrior file</strong> — {(preview as any).has_idcode && !(preview as any).has_smiles
                ? 'idcode columns detected — will attempt conversion to SMILES on import.'
                : 'map your structure column below.'}
            </p>
          </div>
        )}

        {/* PDF - extracted tables with selection + column mapping */}
        {detection.type === 'pdf' && preview.tables && preview.tables.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Extracted Tables</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {preview.page_count} page{preview.page_count !== 1 ? 's' : ''} scanned &middot; {preview.tables.length} table{preview.tables.length !== 1 ? 's' : ''} found
            </p>

            {/* Table selector (when multiple tables) */}
            {preview.tables.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {preview.tables.map((table, tIdx) => (
                  <button
                    key={tIdx}
                    onClick={() => {
                      setSelectedTableIndex(tIdx);
                      // Re-detect column mappings for this table
                      if (preview.columns && tIdx === 0) {
                        // columns are for the largest table (index 0 by default)
                      } else {
                        // Clear mappings; user picks manually
                        const auto: Record<string, string> = {};
                        const t = preview.tables![tIdx];
                        if (t.structure_column_name) {
                          auto[t.structure_column_name] = 'smiles';
                        }
                        setColumnMappings(auto);
                      }
                    }}
                    className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                      selectedTableIndex === tIdx
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 font-medium'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    Table {tIdx + 1} (p.{table.page}, {table.row_count} rows)
                  </button>
                ))}
              </div>
            )}

            {/* Selected table preview */}
            {(() => {
              const table = preview.tables[selectedTableIndex];
              if (!table) return null;
              return (
                <div className="mb-4 overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {table.headers.map((h, i) => (
                          <th key={i} className={`px-3 py-2 text-left font-medium ${
                            table.structure_column_index === i
                              ? 'text-teal-600 dark:text-teal-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {h}
                            {table.structure_column_index === i && (
                              <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 rounded">SMILES</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.slice(0, 8).map((row, rIdx) => (
                        <tr key={rIdx} className="border-t border-gray-100 dark:border-gray-800">
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className="px-3 py-1.5 text-gray-600 dark:text-gray-400 truncate max-w-[150px]">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {table.row_count > 8 && (
                    <p className="text-xs text-gray-400 px-3 py-2">{table.row_count} rows total</p>
                  )}
                </div>
              );
            })()}

            {/* Column mapping moved to dedicated mapping step */}
          </div>
        )}

        {/* PDF with no tables */}
        {detection.type === 'pdf' && (!preview.tables || preview.tables.length === 0) && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-700 dark:text-amber-300">No tabular data detected in this PDF. Try a PDF with SAR tables containing SMILES strings.</p>
          </div>
        )}

        {/* Open Babel conversion note */}
        {preview.converted_from && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Converted from <strong>{preview.converted_from}</strong> format via Open Babel
            </p>
          </div>
        )}

        {/* Info message from backend */}
        {preview.message && !preview.converted_from && detection.type !== 'dwar' && detection.type !== 'pdf' && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">{preview.message}</p>
          </div>
        )}

        {/* FASTA - sequence list */}
        {preview.preview_sequences && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Sequence Preview</h4>
            <div className="space-y-2">
              {preview.preview_sequences.map((seq, i) => (
                <div key={i} className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{seq.header}</p>
                  <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1 break-all">{seq.sequence_preview}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{seq.length.toLocaleString()} residues</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ZIP - archive member list */}
        {preview.archive_entries && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Files in Archive</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {preview.archive_entries.map((entry, i) => (
                <div key={`${entry.name}-${i}`} className="flex items-center justify-between gap-4 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{entry.name}</span>
                  <span className="text-xs text-gray-400 uppercase flex-shrink-0">{entry.type} · {formatBytes(entry.size)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RXN / RDF - text preview */}
        {preview.preview_text && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Reaction Data Preview</h4>
            <pre className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-mono text-gray-600 dark:text-gray-400 overflow-x-auto max-h-48 whitespace-pre-wrap">
              {preview.preview_text}
            </pre>
          </div>
        )}

        {/* PPTX - summary */}
        {detection.type === 'pptx' && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">PowerPoint Summary</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center">
                <p className="text-2xl font-bold text-teal-600">{preview.slide_count || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Slides</p>
              </div>
              <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center">
                <p className="text-2xl font-bold text-teal-600">{preview.total_tables || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Tables</p>
              </div>
              <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center">
                <p className="text-2xl font-bold text-teal-600">{preview.count}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Structures Found</p>
              </div>
            </div>
            {preview.smiles_found && preview.smiles_found.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Detected SMILES:</p>
                <div className="flex flex-wrap gap-2">
                  {preview.smiles_found.map((s, i) => (
                    <span key={i} className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-800 rounded">{s.length > 30 ? s.slice(0, 30) + '...' : s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* PDB */}
        {detection.type === 'pdb' && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Protein Structure</h4>
            <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              {preview.title && <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{preview.title}</p>}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{(preview.atom_count || 0).toLocaleString()} atoms</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Column mapping helpers ────────────────────────────────────────
  const isTabular = !!detection && ['csv', 'xlsx', 'dwar', 'pdf'].includes(detection.type);

  // Group canonical fields by category for dropdown
  const fieldsByCategory = (() => {
    const grouped: Record<string, CanonicalFieldDef[]> = {};
    for (const f of canonicalFields) {
      (grouped[f.category] ??= []).push(f);
    }
    return grouped;
  })();

  const confidenceBadge = (score: number) => {
    if (score >= 0.9) return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
    if (score >= 0.7) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800';
    if (score > 0) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
    return 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700';
  };

  const handleAcceptAllSuggestions = () => {
    const auto: Record<string, string> = {};
    for (const [colName, result] of Object.entries(batchResults)) {
      if (result.canonical_field && result.confidence >= 0.7) {
        auto[colName] = result.canonical_field;
      } else {
        auto[colName] = 'custom';
      }
    }
    setColumnMappings(auto);
  };

  const handleManualMapping = (colName: string, fieldName: string) => {
    setColumnMappings(prev => ({ ...prev, [colName]: fieldName }));
    // Save unrecognized column→field as tenant synonym for next time
    const result = batchResults[colName];
    if (fieldName && fieldName !== 'custom' && result && (!result.canonical_field || result.confidence < 0.7)) {
      fetch('/api/v1/mapping/synonyms', {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ synonym: colName, canonical_field_name: fieldName }),
      }).catch(() => {}); // non-critical
    }
  };

  // Structure field names from canonical fields
  const structureFieldNames = new Set(
    canonicalFields.filter(f => f.is_structure).map(f => f.name)
  );
  // Fallback if fields haven't loaded yet
  if (structureFieldNames.size === 0) {
    ['smiles', 'canonical_smiles', 'inchi', 'molfile'].forEach(n => structureFieldNames.add(n));
  }

  // ── FieldCombobox: typeahead search for field mapping ──────────────
  const FieldCombobox = ({ value, onChange, isStructureCol, hasFields, fieldsByCategory, hint }: {
    value: string;
    onChange: (v: string) => void;
    isStructureCol: boolean;
    hasFields: boolean;
    fieldsByCategory: Record<string, CanonicalFieldDef[]>;
    hint?: string | null;
  }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close on click outside
    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setOpen(false);
          setSearch('');
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Focus input when opened
    useEffect(() => {
      if (open) inputRef.current?.focus();
    }, [open]);

    // Get display label for current value
    const getLabel = (v: string) => {
      if (v === 'custom') return 'Keep as custom';
      if (!v) return 'Skip (discard)';
      for (const fields of Object.values(fieldsByCategory)) {
        const f = fields.find(f => f.name === v);
        if (f) return f.display_name + (f.unit ? ` (${f.unit})` : '');
      }
      // Fallback names
      const fallbacks: Record<string, string> = { smiles: 'SMILES', inchi: 'InChI', cas: 'CAS Number', name: 'Compound Name', formula: 'Molecular Formula' };
      return fallbacks[v] || v;
    };

    // Filter fields by search text
    const q = search.toLowerCase().trim();
    const filteredByCategory: Record<string, CanonicalFieldDef[]> = {};
    if (hasFields) {
      for (const cat of CATEGORY_ORDER) {
        const fields = fieldsByCategory[cat];
        if (!fields) continue;
        const matched = q
          ? fields.filter(f =>
              f.display_name.toLowerCase().includes(q) ||
              f.name.toLowerCase().includes(q) ||
              (f.unit && f.unit.toLowerCase().includes(q))
            )
          : fields;
        if (matched.length > 0) filteredByCategory[cat] = matched;
      }
    }

    const showCustom = !q || 'keep as custom'.includes(q) || 'custom'.includes(q);
    const showSkip = !q || 'skip'.includes(q) || 'discard'.includes(q);

    const select = (v: string) => {
      onChange(v);
      setOpen(false);
      setSearch('');
    };

    return (
      <div ref={containerRef} className="relative min-w-[180px]">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          title={hint || undefined}
          className={`w-full px-3 py-1.5 text-sm border rounded-lg text-left flex items-center justify-between gap-1 focus:outline-none focus:ring-2 focus:ring-teal-400 ${
            isStructureCol
              ? 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 font-medium'
              : value === 'custom'
                ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200'
                : !value
                  ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                  : 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200'
          }`}
        >
          <span className="truncate">{getLabel(value)}</span>
          <svg className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute z-50 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                }}
                placeholder="Search fields..."
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
              />
            </div>

            {/* Options */}
            <div className="max-h-64 overflow-y-auto">
              {/* Fixed options */}
              {showCustom && (
                <button
                  type="button"
                  onClick={() => select('custom')}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2 ${
                    value === 'custom' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 font-medium' : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Keep as custom
                </button>
              )}
              {showSkip && (
                <button
                  type="button"
                  onClick={() => select('')}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 ${
                    !value ? 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  Skip (discard)
                </button>
              )}

              {(showCustom || showSkip) && (Object.keys(filteredByCategory).length > 0 || !hasFields) && (
                <div className="border-t border-gray-100 dark:border-gray-700" />
              )}

              {/* Canonical fields grouped by category */}
              {hasFields ? (
                Object.keys(filteredByCategory).length > 0 ? (
                  CATEGORY_ORDER.filter(cat => filteredByCategory[cat]).map(cat => (
                    <div key={cat}>
                      <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                        {CATEGORY_LABELS[cat] || cat}
                      </div>
                      {filteredByCategory[cat].map(f => (
                        <button
                          key={f.name}
                          type="button"
                          onClick={() => select(f.name)}
                          className={`w-full px-3 py-1.5 text-sm text-left hover:bg-teal-50 dark:hover:bg-teal-900/20 ${
                            value === f.name ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <span>{decodeUnicodeEscapes(f.display_name)}</span>
                          {f.unit && <span className="ml-1.5 text-xs text-gray-400">({f.unit})</span>}
                        </button>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                    No fields match "{search}"
                  </div>
                )
              ) : (
                <>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50">
                    Structure (required)
                  </div>
                  {[{n:'smiles',l:'SMILES'},{n:'inchi',l:'InChI'},{n:'cas',l:'CAS Number'}]
                    .filter(x => !q || x.l.toLowerCase().includes(q) || x.n.includes(q))
                    .map(x => (
                      <button key={x.n} type="button" onClick={() => select(x.n)}
                        className={`w-full px-3 py-1.5 text-sm text-left hover:bg-teal-50 dark:hover:bg-teal-900/20 ${value === x.n ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                      >{x.l}</button>
                    ))
                  }
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50">
                    Identity
                  </div>
                  {[{n:'name',l:'Compound Name'},{n:'formula',l:'Molecular Formula'}]
                    .filter(x => !q || x.l.toLowerCase().includes(q) || x.n.includes(q))
                    .map(x => (
                      <button key={x.n} type="button" onClick={() => select(x.n)}
                        className={`w-full px-3 py-1.5 text-sm text-left hover:bg-teal-50 dark:hover:bg-teal-900/20 ${value === x.n ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                      >{x.l}</button>
                    ))
                  }
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderColumnMapping = () => {
    if (!preview?.columns || !detection) return null;

    const mappedCount = Object.values(columnMappings).filter(v => v && v !== '' && v !== 'custom').length;
    const customCount = Object.values(columnMappings).filter(v => v === 'custom').length;
    const hasFields = canonicalFields.length > 0;

    return (
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Column Mapping</h3>
          <button
            onClick={handleAcceptAllSuggestions}
            disabled={Object.keys(batchResults).length === 0}
            className="px-3 py-1.5 text-xs font-medium text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/40 disabled:opacity-50 transition-colors"
          >
            Accept All Suggestions
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Map each file column to an Adroit field. A structure column (SMILES, InChI, or CAS) or Compound Name is required.
          <span className="ml-2 text-xs">
            {mappedCount} of {preview.columns.length} columns mapped
          </span>
        </p>

        {/* Loading state */}
        {mappingLoading && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-100 dark:border-teal-800">
            <div className="w-4 h-4 border-2 border-teal-300 border-t-teal-600 rounded-full animate-spin" />
            <span className="text-xs text-teal-700 dark:text-teal-300">Matching columns against 150+ known field synonyms...</span>
          </div>
        )}

        {/* Column mapping rows */}
        <div className="space-y-2">
          {preview.columns.map(col => {
            const result = batchResults[col.name];
            const conf = result?.confidence ?? 0;
            const isAutoMatched = !!(result?.canonical_field && conf >= 0.7);
            const currentMapping = columnMappings[col.name] || '';
            const isStructureCol = structureFieldNames.has(currentMapping) || ['cas'].includes(currentMapping);
            const displayHint = result?.display_name
              ? `${result.display_name}${result.unit ? ` (${result.unit})` : ''}`
              : null;

            return (
              <div
                key={col.name}
                className={`flex items-center gap-3 py-2.5 px-4 rounded-lg border transition-all ${
                  isStructureCol
                    ? 'bg-teal-50/50 dark:bg-teal-900/10 border-teal-200 dark:border-teal-800'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}
              >
                {/* Column name + sample values or structure thumbnails */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate block" title={col.name}>
                    {col.name}
                  </span>
                  {isStructureCol && col.sampleValues?.some(v => v != null && thumbnailSvgs[String(v)]) ? (
                    <div className="flex gap-1 mt-1">
                      {col.sampleValues.slice(0, 2).map((v, vi) => {
                        const smi = String(v);
                        const svg = thumbnailSvgs[smi];
                        return svg ? (
                          <div key={vi} className="w-[60px] h-[40px] bg-white rounded border border-gray-200 dark:border-gray-600 overflow-hidden flex items-center justify-center" title={smi}
                            dangerouslySetInnerHTML={{ __html: svg }} />
                        ) : (
                          <span key={vi} className="text-[10px] text-gray-400 font-mono truncate max-w-[80px]">{smi.slice(0, 20)}</span>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500 truncate block" title={col.sampleValues?.map(String).join(', ')}>
                      {col.sampleValues?.[0] != null ? String(col.sampleValues[0]).slice(0, 40) : '—'}
                      {col.sampleValues?.[1] != null ? `, ${String(col.sampleValues[1]).slice(0, 20)}` : ''}
                    </span>
                  )}
                </div>

                {/* Arrow */}
                <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>

                {/* Field mapping — searchable combobox */}
                <FieldCombobox
                  value={currentMapping}
                  onChange={v => handleManualMapping(col.name, v)}
                  isStructureCol={isStructureCol}
                  hasFields={hasFields}
                  fieldsByCategory={fieldsByCategory}
                  hint={displayHint}
                />

                {/* Confidence badge */}
                {conf > 0 ? (
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${confidenceBadge(conf)}`}>
                    {Math.round(conf * 100)}%
                  </span>
                ) : (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 bg-gray-100 dark:bg-gray-800 text-gray-400 border-gray-200 dark:border-gray-700">
                    —
                  </span>
                )}

                {/* Auto badge */}
                {isAutoMatched && currentMapping === result?.canonical_field && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded font-medium flex-shrink-0">
                    Auto
                  </span>
                )}

                {/* Unit badge */}
                {columnUnits[col.name]?.unit && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded font-medium flex-shrink-0">
                    {columnUnits[col.name].unit}
                  </span>
                )}

                {/* Structure indicator */}
                {isStructureCol && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded font-medium flex-shrink-0">
                    Structure
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Validation hint */}
        {!hasSmilesMapping && !hasIdentityMapping && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2">
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs text-amber-700 dark:text-amber-300">
              Map at least one column to SMILES, InChI, CAS, or Compound Name.
            </span>
          </div>
        )}
        {!hasSmilesMapping && hasIdentityMapping && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
            <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs text-blue-700 dark:text-blue-300">
              No structure column mapped. Compounds will be matched by name against existing molecules in the project.
            </span>
          </div>
        )}
      </div>
    );
  };

  // ── Selectivity Panel mapping UI ─────────────────────────────────
  const isPanel = preview?.format === 'selectivity_panel';

  const renderPanelMapping = () => {
    if (!preview) return null;
    const targetCols = preview.target_columns || [];
    const nonTargetCols = preview.non_target_columns || [];
    const selectedCount = panelTargets.size;
    const totalRecords = preview.count * selectedCount;

    const toggleTarget = (t: string) => {
      setPanelTargets(prev => {
        const next = new Set(prev);
        if (next.has(t)) next.delete(t); else next.add(t);
        return next;
      });
    };

    const toggleAll = () => {
      if (panelTargets.size === targetCols.length) {
        setPanelTargets(new Set());
      } else {
        setPanelTargets(new Set(targetCols));
      }
    };

    return (
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Selectivity Panel Detected</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {preview.detected_targets} protein target columns found. This will be imported as bioactivity data.
            </p>
          </div>
        </div>

        {/* Configuration */}
        <div className="grid grid-cols-3 gap-4 mt-6 mb-6">
          {/* ID Column */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">ID Column</label>
            <select
              value={panelIdColumn}
              onChange={e => setPanelIdColumn(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              {nonTargetCols.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Assay Type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Assay Type</label>
            <select
              value={panelAssayType}
              onChange={e => setPanelAssayType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              {['IC50', 'EC50', 'Ki', 'Kd', '% Inhibition', '% Activity'].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Units */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Units</label>
            <select
              value={panelUnit}
              onChange={e => setPanelUnit(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              {['nM', '\u00B5M', 'mM', '%'].map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Target selection */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Target Columns ({selectedCount} of {targetCols.length} selected)
            </h4>
            <button
              onClick={toggleAll}
              className="text-xs text-violet-600 dark:text-violet-400 hover:underline"
            >
              {panelTargets.size === targetCols.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {targetCols.map(t => {
              const selected = panelTargets.has(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleTarget(t)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                    selected
                      ? 'bg-violet-100 dark:bg-violet-900/30 border-violet-300 dark:border-violet-700 text-violet-800 dark:text-violet-200 font-medium'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {selected && (
                    <svg className="w-3.5 h-3.5 inline mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        <div className="p-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-violet-700 dark:text-violet-300">{preview.count}</div>
              <div className="text-xs text-violet-600 dark:text-violet-400">compounds</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-violet-700 dark:text-violet-300">{selectedCount}</div>
              <div className="text-xs text-violet-600 dark:text-violet-400">targets</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-violet-700 dark:text-violet-300">{totalRecords}</div>
              <div className="text-xs text-violet-600 dark:text-violet-400">bioactivity records</div>
            </div>
          </div>
          {preview.has_censored_values && (
            <p className="text-xs text-violet-600 dark:text-violet-400 mt-3 text-center">
              Censored values ({'>'}10000, {'<'}1, etc.) will be stored with qualifier flags.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderProjectAssignment = () => (
    <div className="flex-1 overflow-y-auto px-8 py-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Assign to Project</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Select a project or create a new one.</p>

      {loadingProjects ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-teal-500 rounded-full animate-spin mr-3" />
          Loading projects...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {projects.map(project => {
              const isSelected = selectedProjectId === project.id;
              return (
                <button
                  key={project.id}
                  onClick={() => { setSelectedProjectId(project.id); localStorage.setItem('import:lastProjectId', String(project.id)); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 ring-2 ring-teal-200 dark:ring-teal-800'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color || '#14b8a6' }} />
                    <span className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">{project.name}</span>
                    {isSelected && (
                      <svg className="w-4 h-4 ml-auto text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  {project.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{project.description}</p>}
                </button>
              );
            })}
            {/* New project button */}
            {!showCreateProject && (
              <button
                onClick={() => setShowCreateProject(true)}
                className="p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-teal-300 dark:hover:border-teal-700 text-left transition-all flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-teal-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm font-medium">New Project</span>
              </button>
            )}
          </div>

          {showCreateProject && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-4">
              <input
                type="text"
                value={newProjectName}
                onChange={e => setNewProjectName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateProject()}
                placeholder="Project name..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                autoFocus
              />
              <button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim() || creatingProject}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                {creatingProject ? 'Creating...' : 'Create'}
              </button>
              <button
                onClick={() => { setShowCreateProject(false); setNewProjectName(''); }}
                className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
          )}

          {/* "No project" option */}
          <button
            onClick={() => setSelectedProjectId(null)}
            className={`w-full p-3 rounded-lg border text-left text-sm transition-all ${
              selectedProjectId === null
                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700'
                : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-600'
            }`}
          >
            No project (add to All Files)
          </button>

          {/* Data category toggle */}
          {(screeningResult.isScreeningData || patentResult.isPatentData) && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-medium text-blue-900 dark:text-blue-200 text-sm">
                  Data categories detected
                </span>
              </div>

              {screeningResult.isScreeningData && (
                <div className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                  Screening columns: {screeningResult.matchedColumns.join(', ')}
                </div>
              )}

              {patentResult.isPatentData && (
                <div className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                  Patent columns: {patentResult.matchedColumns.join(', ')}
                </div>
              )}

              <div className="flex flex-col gap-2 mt-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={addToCompounds}
                    onChange={(e) => setAddToCompounds(e.target.checked)}
                    disabled
                    className="rounded border-gray-300"
                  />
                  <span>Add to Compounds</span>
                </label>

                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={addToScreens}
                    onChange={(e) => setAddToScreens(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span>Add to Screens</span>
                  {screeningResult.isScreeningData && (
                    <span className="text-xs text-blue-500">(auto-detected)</span>
                  )}
                </label>

                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={addToPatents}
                    onChange={(e) => setAddToPatents(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span>Add to Patents</span>
                  {patentResult.isPatentData && (
                    <span className="text-xs text-blue-500">(auto-detected)</span>
                  )}
                </label>
              </div>
            </div>
          )}

          {/* Enrichment toggle */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={enrichAfterImport}
                  onChange={e => setEnrichAfterImport(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer-checked:bg-teal-500 transition-colors" />
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Auto-enrich after import</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">Run patent collision scan and target lookup on imported structures</p>
              </div>
            </label>
          </div>
        </>
      )}
    </div>
  );

  const isBackgroundProcessing = importResult?.status === 'processing';

  // ── Dedup wizard step ──────────────────────────────────────────────
  const renderDedupWizard = () => {
    if (!dedupResult) return null;
    const projectName = projects.find(p => p.id === selectedProjectId)?.name || 'this project';
    const newCount = dedupResult.total - dedupResult.match_count;

    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl mx-auto">
          {/* Step 1: Summary */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              We found {dedupResult.total.toLocaleString()} compounds in your file
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {dedupResult.match_count.toLocaleString()} of these match compounds already in <strong>{projectName}</strong>
            </p>
          </div>

          {/* Step 2: Strategy selection */}
          <div className="space-y-3 mb-8">
            <label
              className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                dedupStrategy === 'import_all'
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => setDedupStrategy('import_all')}
            >
              <input type="radio" checked={dedupStrategy === 'import_all'} onChange={() => setDedupStrategy('import_all')} className="mt-1" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Import all &mdash; keep everything as new data</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  All {dedupResult.total.toLocaleString()} compounds will be imported. Duplicates will appear as separate entries.
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                dedupStrategy === 'skip_existing'
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
              onClick={() => setDedupStrategy('skip_existing')}
            >
              <input type="radio" checked={dedupStrategy === 'skip_existing'} onChange={() => setDedupStrategy('skip_existing')} className="mt-1" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Skip existing &mdash; only import new compounds</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {newCount.toLocaleString()} new compounds will be imported. {dedupResult.match_count.toLocaleString()} existing will be skipped.
                </p>
              </div>
            </label>
          </div>

          {/* Step 3: Review matches toggle */}
          {dedupResult.matches.length > 0 && (
            <div>
              <button
                onClick={() => setDedupReviewOpen(!dedupReviewOpen)}
                className="text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 mb-3 flex items-center gap-1"
              >
                <svg className={`w-4 h-4 transition-transform ${dedupReviewOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Review {Math.min(dedupResult.matches.length, 50)} matches
              </button>

              {dedupReviewOpen && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800/50">
                        <th className="text-left px-4 py-2.5 text-gray-500 dark:text-gray-400 font-medium">Your compound</th>
                        <th className="text-left px-4 py-2.5 text-gray-500 dark:text-gray-400 font-medium">Existing compound</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dedupResult.matches.map((match, i) => (
                        <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <StructureImage smiles={match.incoming_smiles} size={60} />
                              <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[120px]">
                                {match.incoming_name || match.incoming_smiles.slice(0, 20)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <StructureImage smiles={match.existing_smiles} size={60} />
                              <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[120px]">
                                {match.existing_name || match.existing_smiles.slice(0, 20)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProgress = () => (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
      {isProcessing ? (
        <>
          {/* Progress animation */}
          <div className="w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-teal-500 animate-spin mb-6" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {uploadPercent !== null && uploadPercent < 100 ? 'Uploading...' : 'Importing...'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            {uploadPercent !== null && uploadPercent < 100
              ? `${formatBytes(file ? Math.round(file.size * uploadPercent / 100) : 0)} / ${formatBytes(file?.size || 0)} (${uploadPercent}%)`
              : `Processing ${file?.name}`
            }
          </p>
          {uploadSpeed !== null && uploadPercent !== null && uploadPercent < 100 && (
            <p className="text-xs text-gray-400 mb-3">{formatBytes(uploadSpeed)}/s</p>
          )}
          {(uploadPercent === null || uploadPercent >= 100) && <div className="mb-3" />}
          {uploadPercent !== null && uploadPercent < 100 && (
            <>
              <div style={{ width: 200, height: 6, borderRadius: 3, background: 'rgba(13, 148, 136, 0.15)', overflow: 'hidden' }}>
                <div style={{
                  width: `${uploadPercent}%`,
                  height: '100%',
                  borderRadius: 3,
                  background: '#0d9488',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <button
                onClick={handleCancelUpload}
                className="mt-3 text-xs text-gray-400 hover:text-red-500 transition-colors"
              >
                Cancel upload
              </button>
            </>
          )}
        </>
      ) : importResult ? (
        <>
          {isBackgroundProcessing ? (
            <>
              {/* Background processing — file queued */}
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">File Queued for Processing</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                {preview?.count ? `${preview.count.toLocaleString()} ${detection?.category === 'reactions' ? 'reactions' : detection?.category === 'sequences' ? 'sequences' : 'structures'}` : 'Your file'} will be processed in the background.
              </p>
              <p className="text-xs text-gray-400 mb-8">
                You can view the file now — data will appear as processing completes.
              </p>
            </>
          ) : (
            <>
              {/* Immediate result — show counts */}
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
                importResult.status === 'partial' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-teal-100 dark:bg-teal-900/30'
              }`}>
                <svg className={`w-8 h-8 ${importResult.status === 'partial' ? 'text-amber-600' : 'text-teal-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {importResult.status === 'partial'
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  }
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {importResult.status === 'partial' ? 'Import finished with failures' : 'Import complete'}
              </h3>

              {/* Breakdown — panel vs standard */}
              {(importResult as any).records_created != null ? (
                <div className="flex gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-violet-600">{(importResult as any).records_created.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Bioactivity Records</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-violet-500">{(importResult as any).compounds}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Compounds</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-violet-400">{(importResult as any).targets}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Targets</p>
                  </div>
                  {(importResult as any).censored_values > 0 && (
                    <div className="text-center">
                      <p className="text-3xl font-bold text-amber-500">{(importResult as any).censored_values}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Censored</p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Headline figure — compounds actually imported */}
                  <div className="flex flex-col items-center mb-3">
                    <p className="text-4xl font-bold text-teal-600">
                      {((importResult.imported_total ?? importResult.success)).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Compounds imported</p>
                  </div>

                  {/* Structure vs catalogue split */}
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 text-center">
                    {((importResult.imported_with_structure ?? importResult.success)).toLocaleString()} with structures
                    {' · '}
                    {((importResult.imported_structureless ?? 0)).toLocaleString()} catalogue / no structure
                  </p>

                  {/* The three outcome classes, each shown only when non-zero.
                      IGNORED is routine housekeeping and stays neutral grey;
                      SKIPPED is informational; only FAILED gets error styling. */}
                  {(() => {
                    // Reason keys are machine-readable; give them plain English.
                    const REASON_LABELS: Record<string, string> = {
                      non_structure_text: 'from slide text',
                      no_structure_column: 'from tables with no structure column',
                      blank_row: 'blank rows',
                      unparseable_structure: 'unparseable structure',
                      insert_error: 'database insert error',
                      duplicate_existing: 'already registered',
                    };
                    const reasonText = (reasons?: Record<string, number>) =>
                      reasons && Object.keys(reasons).length
                        ? ' (' + Object.entries(reasons)
                            .map(([k, v]) => `${v} ${REASON_LABELS[k] ?? k.replace(/_/g, ' ')}`)
                            .join(', ') + ')'
                        : '';

                    const ignored = importResult.ignored_count ?? 0;
                    // Older endpoints report only `failed`; nothing was ever
                    // classed as ignored there, so the fallback is safe.
                    const failed = importResult.failed_count ?? importResult.failed ?? 0;
                    const skipped = importResult.skipped_count
                      ?? (importResult as any).skipped_existing ?? 0;
                    const details = importResult.ignored_details ?? [];

                    return (
                      <>
                        {ignored > 0 && (
                          <p className="text-sm mb-1 text-gray-500 dark:text-gray-400">
                            {ignored.toLocaleString()} non-structure item{ignored === 1 ? '' : 's'} ignored
                            {reasonText(importResult.ignored_reasons)}
                          </p>
                        )}
                        {details.length > 0 && (
                          <ul className="text-xs text-gray-400 dark:text-gray-500 mb-1 list-none">
                            {details.map((d, i) => <li key={i}>{d}</li>)}
                          </ul>
                        )}
                        {skipped > 0 && (
                          <p className="text-sm mb-1 text-gray-600 dark:text-gray-300">
                            {skipped.toLocaleString()} duplicate{skipped === 1 ? '' : 's'} skipped (already registered)
                            {/* The label already says why when that is the only reason. */}
                            {Object.keys(importResult.skipped_reasons ?? {}).length > 1
                              ? reasonText(importResult.skipped_reasons) : ''}
                          </p>
                        )}
                        {failed > 0 && (
                          <p className="text-sm mb-1 font-medium text-red-600 dark:text-red-400">
                            {failed.toLocaleString()} failed to import
                            {reasonText(importResult.failed_reasons)}
                          </p>
                        )}
                      </>
                    );
                  })()}

                  {/* Within-file duplicates collapsed by dedup */}
                  {((importResult.duplicates_merged ?? importResult.duplicates) > 0) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Duplicates merged: {((importResult.duplicates_merged ?? importResult.duplicates)).toLocaleString()}
                    </p>
                  )}

                  {/* Enrichment — non-blocking, separate from the import counts */}
                  {enrichAfterImport && enrichStatus !== 'idle' && (
                    <p className={`text-xs mt-2 ${enrichStatus === 'unavailable' ? 'text-gray-400 dark:text-gray-500' : 'text-teal-600 dark:text-teal-400'}`}>
                      {enrichStatus === 'unavailable' ? '○ Enrichment unavailable' : '◷ Enrichment started'}
                    </p>
                  )}
                </>
              )}

              {/* Failed rows with editable SMILES */}
              {importResult.failures && importResult.failures.length > 0 && (
                <div className="mb-6 w-full max-w-lg">
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-2">
                    {importResult.failures.length} row{importResult.failures.length !== 1 ? 's' : ''} failed to parse
                    {importResult.failed > importResult.failures.length ? ` (showing first ${importResult.failures.length} of ${importResult.failed})` : ''}
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400 font-medium">Row</th>
                          <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400 font-medium">Name</th>
                          <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400 font-medium">SMILES</th>
                          <th className="px-2 py-1.5 text-left text-gray-500 dark:text-gray-400 font-medium">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {importResult.failures.map((f) => (
                          <tr key={f.row} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">{f.row}</td>
                            <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 max-w-[80px] truncate">{f.name || '—'}</td>
                            <td className="px-2 py-1.5">
                              <input
                                type="text"
                                className="w-full px-1.5 py-0.5 text-xs font-mono bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none text-gray-900 dark:text-gray-100"
                                defaultValue={f.smiles}
                                onChange={(e) => setFailureEdits(prev => ({ ...prev, [f.row]: e.target.value }))}
                              />
                            </td>
                            <td className="px-2 py-1.5 text-red-500 dark:text-red-400 max-w-[120px] truncate" title={f.error}>{f.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Show sample errors if no detailed failures */}
              {(!importResult.failures || importResult.failures.length === 0) && (importResult as any).errors?.length > 0 && (
                <div className="mb-6 w-full max-w-md">
                  <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">Sample errors:</p>
                  {(importResult as any).errors.map((err: string, i: number) => (
                    <p key={i} className="text-xs text-gray-500 dark:text-gray-400 truncate">{err}</p>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {importResult.failures && importResult.failures.length > 0 && (
              <button
                onClick={handleRetryFailed}
                disabled={isRetrying}
                className="px-6 py-2.5 bg-amber-600 text-white text-sm font-medium rounded-xl hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isRetrying ? 'Retrying...' : `Retry ${importResult.failures.length} Failed`}
              </button>
            )}
            {importResult.fileId > 0 && detection?.category !== 'sequences' && (
              <button
                onClick={handleGoToData}
                className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors"
              >
                {selectedProjectId
                  ? 'View project →'
                  : importResult.failures && importResult.failures.length > 0
                    ? 'Skip & view compounds →'
                    : 'View compounds →'}
              </button>
            )}
            {selectedProjectId && detection?.category === 'sequences' && (
              <button
                onClick={() => { onComplete?.(); onClose(); navigate(`/chemistry/files?project=${selectedProjectId}&view=files`); }}
                className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors"
              >
                View project →
              </button>
            )}
            <button
              onClick={(importResult as any).records_created != null ? () => { onComplete?.(); onClose(); } : handleImportAnother}
              className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                (importResult as any).records_created != null
                  ? 'bg-violet-600 text-white hover:bg-violet-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {(importResult as any).records_created != null ? 'Done' : 'Import Another'}
            </button>
          </div>
        </>
      ) : error ? (
        <>
          {/* Error */}
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Import Failed</h3>
          <p className="text-sm text-red-500 mb-6 text-center max-w-md">{error}</p>
          <button
            onClick={() => { setStep('preview'); setError(null); }}
            className="px-6 py-2.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-800 transition-colors"
          >
            Go Back
          </button>
        </>
      ) : null}
    </div>
  );

  // ── Units confirmation step ─────────────────────────────────────────
  const renderUnitsConfirmation = () => {
    // Get all non-structure columns from preview (with sample values for categorical detection)
    const filteredColumns = (preview?.columns || [])
      .filter(c => {
        const mapping = columnMappings[c.name];
        return !mapping || !['smiles', 'inchi', 'canonical_smiles', 'molfile', 'cas'].includes(mapping);
      });
    const allColumns = filteredColumns.map(c => c.name);

    // Build sample values map for value-based categorical detection
    const columnSamples: Record<string, any[]> = {};
    for (const col of filteredColumns) {
      if (col.sampleValues && col.sampleValues.length > 0) {
        columnSamples[col.name] = col.sampleValues;
      }
    }

    // Run client-side inference with sample values for categorical detection
    const clientInferred = inferUnits(allColumns, columnSamples);

    // Merge: client categorical/containsUnitValues overrides server; high-confidence
    // client explicit-suffix matches override server default_unit; otherwise server wins
    const mergedUnits: (InferredUnit & { source: 'server' | 'client' })[] = clientInferred.map(ci => {
      // Categorical columns and "contains unit values" columns NEVER get a unit
      // even if the server matched them to a canonical field with a default_unit
      if (ci.isCategorical || ci.containsUnitValues) {
        return { ...ci, source: 'client' as const };
      }

      const serverUnit = columnUnits[ci.columnName];

      // If client found an explicit unit suffix (high confidence), prefer it over
      // server default_unit — the column name literally contains the unit
      if (ci.confidence === 'high' && ci.unit && serverUnit && serverUnit.unit && ci.unit !== serverUnit.unit) {
        return { ...ci, source: 'client' as const };
      }

      // Otherwise, server results take priority (canonical field default_unit)
      if (serverUnit && serverUnit.unit !== undefined) {
        return {
          columnName: ci.columnName,
          unit: serverUnit.unit,
          unitType: serverUnit.unitType || ci.unitType,
          confidence: 'high' as const,
          displayName: serverUnit.displayName || (serverUnit.unit ? `${ci.columnName} (${serverUnit.unit})` : ci.columnName),
          source: 'server' as const,
        };
      }
      return { ...ci, source: 'client' as const };
    });

    const handleUnitChange = (colName: string, unit: string | null, unitType: string) => {
      setColumnUnits(prev => ({
        ...prev,
        [colName]: {
          unit,
          unitType,
          displayName: unit ? `${colName} (${unit})` : colName,
        },
      }));
    };

    const confidenceBadge = (conf: string) => {
      if (conf === 'high') return <span className="inline-block w-2 h-2 rounded-full bg-green-500" title="High confidence" />;
      if (conf === 'medium') return <span className="inline-block w-2 h-2 rounded-full bg-amber-500" title="Medium confidence" />;
      return <span className="inline-block w-2.5 h-0.5 bg-gray-400 rounded" title="No match" />;
    };

    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Confirm Column Units</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Units are auto-detected from column names. Review and adjust if needed, or skip this step.
          </p>
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-2.5 text-gray-500 dark:text-gray-400 font-medium w-5"></th>
                  <th className="text-left px-4 py-2.5 text-gray-500 dark:text-gray-400 font-medium">Column</th>
                  <th className="text-left px-4 py-2.5 text-gray-500 dark:text-gray-400 font-medium">Detected Unit</th>
                  <th className="text-left px-4 py-2.5 text-gray-500 dark:text-gray-400 font-medium w-56">Change</th>
                </tr>
              </thead>
              <tbody>
                {mergedUnits.map((mu, i) => {
                  // Check if user has already overridden this column
                  const userOverride = columnUnits[mu.columnName];
                  const currentUnit = userOverride ? userOverride.unit : mu.unit;
                  const currentUnitType = userOverride ? (userOverride.unitType || mu.unitType) : mu.unitType;
                  const conf = userOverride ? 'high' : mu.confidence;

                  return (
                    <tr key={mu.columnName} className={i % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/30'}>
                      <td className="px-4 py-2.5 text-center">{confidenceBadge(conf)}</td>
                      <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100 font-mono text-xs">{mu.columnName}</td>
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                        {mu.isCategorical
                          ? <span className="text-gray-400 italic">categorical (text)</span>
                          : mu.containsUnitValues
                          ? <span className="text-blue-400 italic">contains unit values</span>
                          : currentUnit || <span className="text-gray-400 italic">{currentUnitType === 'dimensionless' ? 'dimensionless' : 'not specified'}</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <select
                          value={currentUnit === null && currentUnitType === 'dimensionless' ? '__dimensionless__' : (currentUnit || '__none__')}
                          onChange={e => {
                            const val = e.target.value;
                            if (val === '__none__') handleUnitChange(mu.columnName, null, 'unspecified');
                            else if (val === '__dimensionless__') handleUnitChange(mu.columnName, null, 'dimensionless');
                            else {
                              const grp = UNIT_OPTIONS.find(g => g.units.some(u => u.value === val));
                              handleUnitChange(mu.columnName, val, grp?.unitType || 'unspecified');
                            }
                          }}
                          className="w-full text-xs px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                          <option value="__none__">Not specified</option>
                          <option value="__dimensionless__">Dimensionless</option>
                          {UNIT_OPTIONS.map(group => (
                            <optgroup key={group.group} label={group.group}>
                              {group.units.map(u => (
                                <option key={u.value} value={u.value!}>{u.label}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
                {mergedUnits.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400 italic">
                      No data columns to configure units for.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── Step indicator ──────────────────────────────────────────────────
  const steps: { key: Step; label: string }[] = isTabular
    ? [
        { key: 'drop', label: 'Select File' },
        { key: 'preview', label: 'Preview' },
        { key: 'mapping', label: 'Mapping' },
        { key: 'units', label: 'Units' },
        { key: 'project', label: 'Project' },
        { key: 'progress', label: 'Import' },
      ]
    : [
        { key: 'drop', label: 'Select File' },
        { key: 'preview', label: 'Preview' },
        { key: 'project', label: 'Project' },
        { key: 'progress', label: 'Import' },
      ];
  const stepIndex = steps.findIndex(s => s.key === step);

  // Tabular formats require at least one structure or identity column mapped
  const hasSmilesMapping = Object.values(columnMappings).some(v =>
    structureFieldNames.has(v) || ['smiles', 'inchi', 'cas', 'canonical_smiles'].includes(v)
  );
  const hasIdentityMapping = Object.values(columnMappings).some(v =>
    ['name'].includes(v)
  );
  const mappingIncomplete = isTabular && !isPanel && !hasSmilesMapping && !hasIdentityMapping;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Import Data</h2>
          {/* Step indicator */}
          <div className="hidden sm:flex items-center gap-1">
            {steps.map((s, i) => (
              <React.Fragment key={s.key}>
                {i > 0 && <div className="w-6 h-px bg-gray-300 dark:bg-gray-600" />}
                <span className={`text-xs px-2 py-1 rounded-full transition-colors ${
                  i === stepIndex ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 font-medium'
                    : i < stepIndex ? 'text-teal-600'
                    : 'text-gray-400'
                }`}>
                  {s.label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <button
          onClick={onClose}
          disabled={isProcessing}
          className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-400 disabled:opacity-50 transition-colors rounded-lg hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Error banner */}
      {error && step !== 'progress' && (
        <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 flex items-center gap-3">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-red-700 flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {echoHandoff ? <div className="flex-1 overflow-auto px-6 py-10 bg-slate-50 dark:bg-gray-950">
          <div className="mx-auto max-w-3xl rounded-2xl border border-teal-200 bg-white dark:bg-gray-900 dark:border-teal-800 shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-teal-600 to-cyan-400" />
            <div className="p-7 sm:p-9">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center text-2xl">&#129514;</div>
                <div>
                  <div className="text-xs font-semibold tracking-widest uppercase text-teal-700">Echo assay data detected</div>
                  <h3 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">This file belongs with an assay run</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">We recognised <b>{echoHandoff.file.name}</b> as {echoHandoff.kind === 'workbook' ? 'a complete Echo workbook package' : 'an Echo completed-transfer log'}. It will not be treated as a compound-library spreadsheet.</p>
                </div>
              </div>
              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                {['Select the registered assay', 'Choose the correct assay run', 'Import, review blockers and commit'].map((label, index) => <div key={label} className="rounded-xl bg-slate-50 dark:bg-gray-800 p-4"><div className="text-xs font-bold text-teal-700">STEP {index + 1}</div><div className="mt-1 text-sm font-medium text-slate-800 dark:text-slate-100">{label}</div></div>)}
              </div>
              <div className="mt-7 flex flex-wrap justify-end gap-3">
                <button onClick={() => { setEchoHandoff(null); setFile(null); setPreview(null); setStep('drop'); }} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200">Choose another file</button>
                <button onClick={() => { const handoff = echoHandoff; onClose(); navigate('/biology/assays', { state: { echoImportFilename: handoff.file.name, echoImportWorkbook: handoff.kind === 'workbook' } }); }} className="rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700">Continue to Assays →</button>
              </div>
            </div>
          </div>
        </div> : step === 'drop' && renderDropZone()}
        {step === 'preview' && renderPreview()}
        {step === 'mapping' && (isPanel ? renderPanelMapping() : renderColumnMapping())}
        {step === 'units' && renderUnitsConfirmation()}
        {step === 'project' && renderProjectAssignment()}
        {step === 'dedup' && renderDedupWizard()}
        {step === 'progress' && renderProgress()}
      </div>

      {/* Footer navigation */}
      {step !== 'progress' && !echoHandoff && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={() => {
              if (step === 'drop') { onClose(); return; }
              if (step === 'preview') { setStep('drop'); return; }
              if (step === 'mapping') { setStep('preview'); return; }
              if (step === 'units') { setStep('mapping'); return; }
              if (step === 'project') { setStep(isTabular ? 'units' : 'preview'); return; }
              if (step === 'dedup') { setStep('project'); return; }
            }}
            className="px-5 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            {step === 'drop' ? 'Cancel' : 'Back'}
          </button>

          {step === 'preview' && (
            <button
              onClick={() => setStep(isTabular ? 'mapping' : 'project')}
              className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors"
            >
              Next: {isTabular ? 'Map Columns' : 'Choose Project'}
            </button>
          )}

          {step === 'mapping' && (
            <button
              onClick={() => setStep('units')}
              disabled={isPanel ? panelTargets.size === 0 : mappingIncomplete}
              className={`px-6 py-2.5 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                isPanel ? 'bg-violet-600 hover:bg-violet-700' : 'bg-teal-600 hover:bg-teal-700'
              }`}
              title={isPanel
                ? (panelTargets.size === 0 ? 'Select at least one target' : undefined)
                : (mappingIncomplete ? 'Map at least one column to SMILES, InChI, CAS, or Compound Name' : undefined)
              }
            >
              Next: Confirm Units
            </button>
          )}

          {step === 'units' && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep('project')}
                className="px-5 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={() => setStep('project')}
                className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors"
              >
                Next: Choose Project
              </button>
            </div>
          )}

          {step === 'project' && (
            <button
              onClick={handleCheckDuplicates}
              disabled={dedupChecking || showCreateProject || creatingProject}
              title={showCreateProject ? 'Create or cancel the new project first' : undefined}
              className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {creatingProject ? (
                'Creating project...'
              ) : showCreateProject ? (
                'Create project first'
              ) : dedupChecking ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Checking...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Import {preview?.count.toLocaleString() || ''} {detection?.category === 'sequences' ? 'sequences' : detection?.category === 'reactions' ? 'reactions' : 'structures'}
                </>
              )}
            </button>
          )}

          {step === 'dedup' && (
            <button
              onClick={handleCommit}
              className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-xl hover:bg-teal-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {dedupStrategy === 'skip_existing'
                ? `Import ${(dedupResult ? dedupResult.total - dedupResult.match_count : 0).toLocaleString()} new compounds`
                : `Import all ${dedupResult?.total.toLocaleString() || ''} compounds`
              }
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedImportDialog;
